import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  operations,
  type Operation,
  type OperationInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Icon } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

import {
  filtrosVazios,
  permissoesDaCotacao,
  quantosFiltros,
  type QuoteFilters,
} from './cotacao';
import { DraftPrintDialog } from './DraftPrintDialog';
import { ListaDeOperacoes } from './listaDeOperacoes';
import { OperationFiltersDialog } from './OperationFiltersDialog';
import { OperationForm } from './OperationForm';
import { QuoteSettingsDialog } from './QuoteSettingsDialog';
import { BrokerQuoteSettingsDialog } from './quote/BrokerQuoteSettingsDialog';
import { BrokerQuotesPage } from './quote/BrokerQuotesPage';
import { PartyToolbar } from '../shared/PartyToolbar';

const etapas = [
  { value: '', label: 'Todas as etapas' },
  'em_cotacao',
  'aguardando_credito',
  'em_subscricao',
  'em_juridico',
  'aguardando_comercial',
  'minuta_gerada',
  'aguardando_ccg',
  'aguardando_assinatura',
  'em_emissao',
  'emitida',
  'declinada',
].map((item) =>
  typeof item === 'string'
    ? { value: item, label: statusLabel('operacao', item) }
    : item,
);

const limite = 25;

/**
 * `/operacoes`, com a visao por perfil (decisao 2 da 11.11, P-NCT): a corretora ve a
 * lista de cotacoes dela, com as colunas e os filtros do OnPoint; a seguradora continua
 * com a lista de operacoes.
 */
export function OperationsPage() {
  const { session } = useAuth();
  return session?.broker_scoped === true ? (
    <BrokerQuotesPage />
  ) : (
    <InsurerOperationsPage />
  );
}

function InsurerOperationsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [busca, setBusca] = useState('');
  // A etapa pode vir da URL: e assim que o funil do painel leva a lista ja filtrada.
  // Ler o parametro tambem faz o endereco ser compartilhavel, que e o que se espera
  // de uma lista filtrada.
  const [parametros, setParametros] = useSearchParams();
  const etapa = parametros.get('stage') ?? '';

  function setEtapa(valor: string) {
    setParametros(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        if (valor) proximos.set('stage', valor);
        else proximos.delete('stage');
        return proximos;
      },
      { replace: true },
    );
  }
  const [somenteVencidas, setSomenteVencidas] = useState(false);
  const [offset, setOffset] = useState(0);
  const [criando, setCriando] = useState(false);
  // Filtros do dialogo "Cotacoes" do OnPoint (P-COT-1), aplicados de uma vez.
  const [filtros, setFiltros] = useState<QuoteFilters>(filtrosVazios);
  const [filtrando, setFiltrando] = useState(false);
  const [configurando, setConfigurando] = useState(false);
  const [configurandoCorretora, setConfigurandoCorretora] = useState(false);
  // "Imprimir minuta" do menu da linha (COT-005, P-COT-2).
  const [imprimindo, setImprimindo] = useState<Operation | null>(null);
  const aplicados = quantosFiltros(filtros);
  const filtrado = busca !== '' || etapa !== '' || somenteVencidas || aplicados > 0;

  const podeCriar = can('operacoes.operacao.create');
  const podeConfigurar = can(permissoesDaCotacao.parametros);
  const podeImprimir = can('minutas.minuta.view') || can('minutas.minuta.create');

  const consulta = useQuery({
    queryKey: ['operations', { busca, etapa, somenteVencidas, offset, filtros }],
    queryFn: ({ signal }) =>
      operations.list(
        {
          q: busca,
          stage: etapa,
          overdue: somenteVencidas ? 'true' : undefined,
          limit: limite,
          offset,
          ...filtros,
          big_risk: filtros.big_risk ? 'true' : undefined,
          monitoring: filtros.monitoring ? 'true' : undefined,
          step_in: filtros.step_in ? 'true' : undefined,
        },
        signal,
      ),
  });

  const criar = useMutation({
    mutationFn: (input: OperationInput) => operations.create(input),
    onSuccess: (operacao) => {
      toast.success(`Operacao ${operacao.number} aberta.`);
      setCriando(false);
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void navigate(`/operacoes/${operacao.id}`);
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Operacoes"
        description="Esteira da operacao, do pedido a emissao."
        actions={
          <>
            {podeConfigurar ? (
              <Button variant="secondary" onClick={() => setConfigurando(true)}>
                <Icon name="settings" size={15} />
                Configuracao da cotacao
              </Button>
            ) : null}
            {podeConfigurar ? (
              <Button
                variant="secondary"
                onClick={() => setConfigurandoCorretora(true)}
              >
                Cotacao da corretora
              </Button>
            ) : null}
            {podeCriar ? (
              <Button onClick={() => setCriando(true)}>
                <Icon name="sparkle" size={15} />
                Nova operacao
              </Button>
            ) : null}
          </>
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as operacoes">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por numero, objeto, tomador ou corretora"
          filter={
            <Select
              label="Etapa"
              className="h-9"
              options={etapas}
              value={etapa}
              onChange={(event) => {
                setEtapa(event.target.value);
                setOffset(0);
              }}
            />
          }
          extra={
            <>
              <Button
                size="sm"
                variant="secondary"
                className="self-end"
                onClick={() => setFiltrando(true)}
              >
                {aplicados > 0 ? `Filtros (${aplicados})` : 'Filtros'}
              </Button>
              <label className="flex h-9 cursor-pointer items-center gap-2 self-end text-xs text-ink-600">
                <input
                  type="checkbox"
                  checked={somenteVencidas}
                  onChange={(event) => {
                    setSomenteVencidas(event.target.checked);
                    setOffset(0);
                  }}
                  className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
                />
                Somente prazo vencido
              </label>
            </>
          }
        />

        <ListaDeOperacoes
          items={consulta.data?.items ?? []}
          loading={consulta.isPending}
          onAbrir={(operacao) => void navigate(`/operacoes/${operacao.id}`)}
          acoes={{
            podeImprimir,
            onVisualizar: (operacao) => void navigate(`/operacoes/${operacao.id}`),
            onImprimir: setImprimindo,
          }}
          empty={
            <EmptyState
              icon="clock"
              title={
                filtrado ? 'Nenhuma operacao encontrada' : 'Nenhuma operacao aberta'
              }
              description={
                filtrado
                  ? 'Ajuste os filtros para ver outras operacoes.'
                  : 'Uma operacao nasce na analise de credito e percorre a esteira ate a emissao. Cadastre tomador, produto e modalidade antes de abrir a primeira.'
              }
              action={
                podeCriar && !filtrado ? (
                  <Button onClick={() => setCriando(true)}>Abrir operacao</Button>
                ) : null
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <OperationFiltersDialog
        open={filtrando}
        value={filtros}
        onClose={() => setFiltrando(false)}
        onApply={(novos) => {
          setFiltros(novos);
          setOffset(0);
          setFiltrando(false);
        }}
      />

      {imprimindo ? (
        <DraftPrintDialog
          operationId={imprimindo.id}
          title={`Imprimir minuta da cotacao ${imprimindo.proposal_number ?? imprimindo.number}`}
          onClose={() => setImprimindo(null)}
        />
      ) : null}

      {podeConfigurar ? (
        <QuoteSettingsDialog
          open={configurando}
          onClose={() => setConfigurando(false)}
        />
      ) : null}

      {podeConfigurar ? (
        <BrokerQuoteSettingsDialog
          open={configurandoCorretora}
          onClose={() => setConfigurandoCorretora(false)}
        />
      ) : null}

      <Modal
        open={criando}
        onClose={() => setCriando(false)}
        title="Nova operacao"
        description="A operacao nasce na analise de credito."
        size="lg"
      >
        <OperationForm
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      </Modal>
    </div>
  );
}
